import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useCallback, useState } from 'react';
import type { HandlebarsBlockType } from '../../types';

export const HandlebarsBlockEndView: React.FC<NodeViewProps> = ({
  node,
  selected,
  editor,
  getPos,
  deleteNode,
}) => {
  const { blockType, blockId, description, isValid } = node.attrs as {
    blockType: HandlebarsBlockType;
    blockId: string;
    description: string | null;
    isValid: boolean;
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const tagClasses = [
    'handlebars-block-end',
    `block-${blockType}`,
    selected ? 'selected' : '',
    !isValid ? 'invalid' : '',
    description ? 'has-description' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    editor.commands.focus();
  }, [editor]);

  const handleDeleteBlockOnly = useCallback(() => {
    // Delete only the start and end tags, preserving content
    const pos = getPos();
    if (typeof pos !== 'number') return;

    // Find and delete matching start tag first
    editor.state.doc.descendants((startNode, startPos) => {
      if (
        startNode.type.name === 'handlebarsBlockStart' &&
        startNode.attrs.blockId === blockId
      ) {
        editor.chain().focus().deleteRange({
          from: startPos,
          to: startPos + startNode.nodeSize,
        }).run();
        return false;
      }
    });

    // Then delete this end tag
    deleteNode();
    setShowDeleteConfirm(false);
  }, [editor, getPos, blockId, deleteNode]);

  const handleDeleteAll = useCallback(() => {
    const pos = getPos();
    if (typeof pos !== 'number') return;

    let startPos = -1;

    // Find matching start tag position
    editor.state.doc.descendants((startNode, nodePos) => {
      if (
        startNode.type.name === 'handlebarsBlockStart' &&
        startNode.attrs.blockId === blockId
      ) {
        startPos = nodePos;
        return false;
      }
    });

    if (startPos >= 0 && startPos < pos) {
      // Delete everything from start to end (inclusive)
      editor
        .chain()
        .focus()
        .deleteRange({ from: startPos, to: pos + node.nodeSize })
        .run();
    } else {
      // No matching start found, just delete the end node
      deleteNode();
    }

    setShowDeleteConfirm(false);
  }, [editor, getPos, blockId, node.nodeSize, deleteNode]);

  return (
    <NodeViewWrapper as="span" className="handlebars-block-end-wrapper">
      <span
        className={tagClasses}
        title={`{{/${blockType}}}`}
      >
        <span className="block-indicator">/</span>
        <span className="block-type">{description || blockType}</span>
      </span>

      {showDeleteConfirm && (
        <span className="block-delete-confirmation" contentEditable={false}>
          <span className="message">Delete block?</span>
          <span className="actions">
            <button type="button" onClick={handleDeleteBlockOnly}>
              Keep content
            </button>
            <button type="button" className="danger" onClick={handleDeleteAll}>
              Delete all
            </button>
            <button type="button" onClick={handleDeleteCancel}>
              Cancel
            </button>
          </span>
        </span>
      )}
    </NodeViewWrapper>
  );
};

export default HandlebarsBlockEndView;
